// Per spec/blocks.md §12. Sponsor showcase.
//
// **P-037.23 / D-131 — drop-and-replace.** Old shape
// (name / tagline / description / websiteUrl + `options.layout` enum)
// replaced wholesale with a fork of event-card-group's post-D-130 shape:
//
// - `optionsSchema = z.object({})` — no layout option; single 3-col rhythm
//   for all sponsor lists (matches event-card-group / events-list).
// - `inputsSchema.sponsors[]` flattens to: `sponsorName` (was `name`),
//   `logoUrl` (kept, now `.optional()` per D-131 fallback contract),
//   `caption` (was `location` in EventCard — plain 14 px text, EventCard-
//   location parity), `sponsorWebsiteUrl` (was `ctaUrl`).
// - `dateText` from EventCard has no sponsor analogue → DROPPED.
// - `ctaLabel` from EventCard → DROPPED (no per-sponsor CTA enum; the
//   sponsor name itself carries the CTA semantics — see SponsorCard).
// - Render: same 3-col `gridLayout(items, 3, ...)` inside a 30 px L/R +
//   30 px top padded outer cell as event-card-group. EventCard + new
//   SponsorCard visually share rhythm — Big 3 + Sponsor sections compose
//   cleanly.
//
// **Field mapping (EventCard → SponsorCard):**
//   title       → sponsorName
//   imageUrl    → logoUrl (optional + fallback)
//   location    → caption (plain text)
//   ctaUrl      → sponsorWebsiteUrl (name-as-link semantic)
//   dateText    → DROPPED
//   ctaLabel    → DROPPED
//
// **Pre-D-131 instances dropped at v7 → v8.** The pre-D-131 chapter-
// sponsors shape (name / tagline / description / websiteUrl + options.
// layout) doesn't map cleanly to the new shape — Banks's "Remove
// current block" framing = drop existing instances + start fresh. The
// schema migration filters every pre-D-131 chapter-sponsors block out
// of every blocks[] array; new-shape instances persist intact. Same
// playbook as D-130's `other-notable-events` drop.
//
// Auto-hide: orchestrator-level skip on `sponsors.length === 0`.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { sponsorCard, type SponsorCardProps } from '../components/sponsor-card'
import { gridLayout, pickDefined } from '../components/helpers'

const sponsorItemSchema = z.object({
  sponsorName: z.string().min(1),
  logoUrl: z.string().url().describe('imageUrl').optional(),
  caption: z.string().min(1).optional(),
  sponsorWebsiteUrl: z.string().url().optional(),
})

type SponsorItem = z.infer<typeof sponsorItemSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  sponsors: z.array(sponsorItemSchema).max(8),
})

function sponsorToProps(item: SponsorItem, linkColor: string): SponsorCardProps {
  return {
    sponsorName: item.sponsorName,
    linkColor,
    ...pickDefined({
      logoUrl: item.logoUrl,
      caption: item.caption,
      sponsorWebsiteUrl: item.sponsorWebsiteUrl,
    }),
  }
}

export const chapterSponsors = defineBlock({
  id: 'chapter-sponsors',
  label: 'Chapter Sponsors',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { sponsors: [] },
  autoHide: ({ inputs }) => inputs.sponsors.length === 0,
  render: ({ inputs }, ctx) => {
    const grid = gridLayout(inputs.sponsors, 3, (s) =>
      sponsorCard(sponsorToProps(s, ctx.linkColor)),
    )
    // 30 px L/R padded outer cell — mirrors events-list / event-card-
    // group. Top/bottom padding lives on the system-level inter-Block
    // spacer (`src/render/block-padding.ts`) per P-037.29 D-137 §item 2.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${grid}</td></tr></table>`
  },
})
